import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ApiError } from "../api/types";
import { useAuth } from "../store/auth";
import { useLocale } from "../hooks/useLocale";
import styles from "./LoginPage.module.css";

export function ResetPasswordPage() {
  const { t, tOr } = useLocale();
  const resetPassword = useAuth((s) => s.resetPassword);
  const navigate = useNavigate();
  const location = useLocation();

  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [errCode, setErrCode] = useState<string | null>(null);

  useEffect(() => {
    const tok = new URLSearchParams(location.search).get("token") ?? "";
    setToken(tok);
    if (!tok) setErrCode("token_invalid");
  }, [location.search]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy || !token) return;
    setErrCode(null);

    if (password !== confirm) {
      setErrCode("password_mismatch");
      return;
    }
    setBusy(true);
    try {
      await resetPassword(token, password);
      // Reset succeeded — bounce to login with a confirmed-style flash.
      navigate("/login?confirmed=1", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setErrCode(err.code);
      else setErrCode("unknown");
      setBusy(false);
    }
  }

  return (
    <main className={styles.shell}>
      <form className={styles.card} onSubmit={onSubmit}>
        <h1 className={styles.title}>{t("reset.title")}</h1>
        <p className={styles.subtitle}>{t("reset.subtitle")}</p>
        <label className={styles.label}>
          <span>{t("reset.password")}</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
            maxLength={72}
          />
        </label>
        <label className={styles.label}>
          <span>{t("reset.confirm")}</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
            maxLength={72}
          />
        </label>
        {errCode && (
          <p className={styles.error}>
            {tOr(`reset.error.${errCode}`, "reset.error.unknown")}
          </p>
        )}
        <button type="submit" className={styles.submit} disabled={busy || !token}>
          {busy ? t("common.loading") : t("reset.submit")}
        </button>
        <div className={styles.links}>
          <Link to="/login">{t("reset.link.back_to_login")}</Link>
        </div>
      </form>
    </main>
  );
}
